import { useState } from "react";

/**
 * State that resets itself when the thing it belongs to changes.
 *
 * Four views hold a piece of state *about* a prop — which image is in the main
 * slot, which ones failed to fetch, which one the dialog is showing — and every
 * one of them has to answer the same question: what happens when the prop
 * changes underneath it?
 *
 * **Reset during render, not in an effect.** This is React's own answer for
 * "adjust state when a prop changes", and it is what
 * `react-hooks/set-state-in-effect` points at: the effect version renders the
 * stale state once and then corrects itself, which on a gallery means showing
 * the previous item's picture for a frame.
 *
 * **Key on the set, not on a URL.** The mistake this shape exists to prevent is
 * a flag that remembers *which* value failed and asks whether the current one is
 * it. That looks equivalent and is not: it remembers A forever, so A -> B -> A —
 * which on these screens is clicking a variant and clicking back — never
 * re-attempts A, and a 404 since fixed could never recover for as long as the
 * page lived. A key derived from the whole set has no such memory: a different
 * set is a different key, and the same set is genuinely the same state.
 *
 * The key is compared with `Object.is`, so callers pass something already
 * comparable — a string join of the ids, a single URL — rather than an object
 * literal rebuilt each render.
 *
 * The setter takes `useState`'s updater form for `useState`'s reason: two events
 * in one batch — two `onError`s from two pictures — both read the same render's
 * value, so the second write clobbers the first unless it is derived from what
 * the first actually left behind. It carries `useState`'s caveat with it: a `T`
 * that is itself a function has to be written through an updater.
 */
export function useResetOnKeyChange<T>(
  key: unknown,
  initial: T,
): [T, (next: T | ((previous: T) => T)) => void] {
  const [state, setState] = useState({ key, value: initial });

  let value = state.value;
  if (!Object.is(state.key, key)) {
    // The setter below writes under *this* render's key, so a reset and a write
    // in the same render both land under the new one.
    setState({ key, value: initial });
    value = initial;
  }

  const set = (next: T | ((previous: T) => T)) =>
    setState((previous) => {
      // The reset may not have committed when a same-batch write lands, so an
      // updater reads through to the value the new key actually starts from.
      const base = Object.is(previous.key, key) ? previous.value : initial;
      return { key, value: typeof next === "function" ? (next as (p: T) => T)(base) : next };
    });

  return [value, set];
}
